import { useState } from 'react';
import { Button, Drawer, Empty, Modal, Upload } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import type { RcFile } from 'antd/es/upload/interface';
import { useBulkExtraction, type BulkItem } from '@/hooks/useBulkExtraction';
import { BulkQueueItem } from './BulkQueueItem';
import { ReviewForm } from './ReviewForm';

interface Props {
  open: boolean;
  onClose: () => void;
  onAnySaved: () => void;
  investorOptions: string[];
}

export function BulkUploadFlow({ open, onClose, onAnySaved, investorOptions }: Props) {
  const { items, addFiles, markSaved, markSkipped, reset } = useBulkExtraction();
  const [reviewing, setReviewing] = useState<BulkItem | null>(null);

  // Re-derive the live item being reviewed (so phase/result updates are
  // reflected if the user reopens the drawer mid-flight).
  const liveReviewing = reviewing
    ? (items.find((it) => it.id === reviewing.id) ?? reviewing)
    : null;

  return (
    <>
      <Modal
        open={open}
        title="Upload many flyers"
        width={800}
        onCancel={() => {
          reset();
          onClose();
        }}
        footer={null}
        destroyOnHidden
      >
        <Upload.Dragger
          multiple
          accept=".jpg,.jpeg,.png,.pdf"
          beforeUpload={(file: RcFile, list: RcFile[]) => {
            // beforeUpload runs once per file; flush the whole batch on the
            // last one so we add all files in one shot.
            if (file === list[list.length - 1]) addFiles(list);
            return false;
          }}
          showUploadList={false}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">Drop multiple flyers here (or click to pick)</p>
          <p className="ant-upload-hint">Up to 3 process in parallel</p>
        </Upload.Dragger>

        <div style={{ marginTop: 16, maxHeight: 400, overflowY: 'auto' }}>
          {items.length === 0 && <Empty description="Queue is empty" />}
          {items.map((it) => (
            <BulkQueueItem
              key={it.id}
              item={it}
              onReview={() => setReviewing(it)}
              onSkip={() => markSkipped(it.id)}
            />
          ))}
        </div>

        <div style={{ marginTop: 16, textAlign: 'right' }}>
          <Button
            onClick={() => {
              reset();
              onClose();
            }}
          >
            Done
          </Button>
        </div>
      </Modal>

      <Drawer
        open={!!reviewing}
        width="80%"
        onClose={() => setReviewing(null)}
        title={reviewing?.file.name}
        destroyOnHidden
      >
        {liveReviewing?.result?.draft && (
          <ReviewForm
            draft={liveReviewing.result.draft}
            confidence={liveReviewing.result.confidence ?? 0}
            warnings={liveReviewing.result.warnings ?? []}
            attachmentId={liveReviewing.attachmentId}
            flyerPreviewUrl={liveReviewing.previewUrl}
            investorOptions={investorOptions}
            onSaved={() => {
              markSaved(liveReviewing.id);
              onAnySaved();
              setReviewing(null);
            }}
            onDiscard={() => setReviewing(null)}
          />
        )}
      </Drawer>
    </>
  );
}
